<template>
	<VideoPlayerWrapper
		:isDisneyPlusOffer="isDisneyPlusOffer"
		:isActive="isActive"
		:isBackdrop="isBackdrop"
		:playIconType="playIconType"
		:isDMTitleTrailer="isTitleTrailer"
		@play="play"
		:class="{ 'video-player--bitmovin': isBitMovinVideo }"
	>
		<template #image-preview>
			<slot name="image-preview"></slot>
		</template>
		<template #play-btn v-if="isJustWatchTV">
			<JustWatchTVPlayBtn v-show="isMounted" />
		</template>
		<template #video-player>
			<iframe
				v-if="isActive && !isBitMovinVideo"
				frameborder="0"
				width="100%"
				height="100%"
				referrerpolicy="no-referrer-when-downgrade"
				:src="srcUrl"
				allowfullscreen
				allow="autoplay"
			></iframe>

			<BitmovinPlayer
				v-else-if="isActive && isBitMovinVideo"
				:title="title"
				:clip="clip"
				:additionalContexts="props.additionalContexts"
			/>
		</template>
	</VideoPlayerWrapper>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue';
import { PlayerIcon } from '@/enums/player-icon';

import VideoPlayerWrapper from '@/ui-components/VideoPlayerWrapper.vue';
import { useMounted } from '@vueuse/core';
import { TrailerClip } from '@/interfaces/trailerClip';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { Clip, ClipProvider } from '@/@types/graphql-types';

import Spinner from '@/ui-components/Spinner.vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { storeToRefs } from 'pinia';
import { SnowplowContext } from '@/helpers/tracking/providers';

const BitmovinPlayer = defineAsyncComponent({
	loader: () => import(/* webpackChunkName: "bitmovin-player" */ './BitmovinPlayer.vue'),
	loadingComponent: Spinner,
});
const JustWatchTVPlayBtn = defineAsyncComponent(() => import('@/pages/titleDetailV1/v1/hero/JustwatchTVPlayBtn.vue'));

interface DailyMotionPlayerProps {
	clip: TrailerClip | Clip;
	title?: TitleDetail;
	isActive?: boolean;
	isBackdrop?: boolean;
	playIconType?: PlayerIcon | null;
	isDisneyPlusOffer?: boolean;
	isTitleTrailer?: boolean;
	additionalContexts?: SnowplowContext[];
	hideTrailerAd?: boolean;
}

const isMounted = useMounted();
const { isPremium } = storeToRefs(useUserStore());

const props = withDefaults(defineProps<DailyMotionPlayerProps>(), {
	isActive: false,
	isBackdrop: false,
	playIconType: null,
	isDisneyPlusOffer: false,
	hideTrailerAd: false,
});

const emit = defineEmits<{
	(e: 'setActiveClipId', clipId: string): void;
	(e: 'onPlaying', clip: DailyMotionPlayerProps['clip']): void;
}>();

const playerIdsByType = {
	DEFAULT_TRAILERS: 'xb9cu',
	NO_ADS_TRAILERS: 'x1dwv8',

	JUSTWATCH_TV_DEFAULT: 'x10mxm',
	JUSTWATCH_TV_NO_ADS: 'x1b8c8',
	JUSTWATCH_TV_NO_AUTO_PLAY: 'x1bfya',
};

const isJustWatchTV = computed(() => props.clip.provider === 'JUSTWATCH_TV');
const isBitMovinVideo = computed(() => {
	return (
		props.clip.provider === 'OTT' ||
		props.clip.provider === ClipProvider.Justwatchtv ||
		isJustWatchTV.value ||
		props.clip.sourceUrl?.includes('dailymotion.com/cdn/manifest') ||
		props.clip.externalId === 'b2Z8dG04Nzg3MjpERTpqd3Q6ZnJlZTpoZA=='
	);
});
const playerId = computed(() => {
	if (isJustWatchTV.value) {
		if (isPremium.value) {
			return playerIdsByType['JUSTWATCH_TV_NO_ADS'];
		}
		return playerIdsByType['JUSTWATCH_TV_DEFAULT'];
	}
	if (props.hideTrailerAd) {
		return playerIdsByType['NO_ADS_TRAILERS'];
	}
	return playerIdsByType['DEFAULT_TRAILERS'];
});
const srcUrl = computed(() => {
	if (isPremium.value && isJustWatchTV.value && props.clip.sourceUrl) {
		const url = new URL(props.clip.sourceUrl);
		const externalId = url.searchParams.get('video');
		if (externalId) {
			// prettier-ignore
			return `${props.clip.sourceUrl.replace(playerIdsByType['JUSTWATCH_TV_DEFAULT'], playerIdsByType['JUSTWATCH_TV_NO_ADS'])}`;
		}
	}

	if (props.clip.sourceUrl && !props.hideTrailerAd) return props.clip.sourceUrl;

	return `https://geo.dailymotion.com/player/${playerId.value}.html?video=${props.clip.externalId}`;
});

function play() {
	emit('setActiveClipId', props.clip.externalId);

	// onPlaying triggers scrolling to active clip. setTimeout prevents onPlaying triggering scroll events before activeClip is updated
	setTimeout(() => {
		emit('onPlaying', props.clip);
	}, 200);
}
</script>

<style lang="scss" scoped>
.video-player--bitmovin {
	display: flex;
	padding: 0px !important;
	:deep(.video-player__video-container) {
		flex: 1;
	}
}
</style>
